import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const PricingTab = ({
  formData,
  handleChange,
  suppliersList,
}) => {
  const { isAdmin } = useAuth();
  const priceLocked = !!formData.price_locked;
  const priceReadOnly = priceLocked && !isAdmin;
  // Local state for inputs to prevent circular updates during typing
  const [localMarkup, setLocalMarkup] = useState(formData.markup_percent ?? "");
  const [localSellingPrice, setLocalSellingPrice] = useState(formData.selling_price_incl_vat || formData.retail_price_level1 || "");
  const [localCostPrice, setLocalCostPrice] = useState(formData.last_cost_price_excl_vat || formData.cost_price || "");
  
  // Get VAT rate at the PRODUCT level. Only truly zero-rated items are 0%
  // on the shelf. "Exempt" products still ship with 15% VAT baked into the
  // shelf price; VAT is only stripped at the till for customers with a
  // valid exemption certificate. See Sales.jsx for the till-side logic.
  const getVatRate = (vatCategory) => {
    const vat = vatCategory || formData.vat_category || "Standard 15%";
    if (vat === "Zero-rated 0%") return 0;
    return 0.15;
  };

  // Calculate markup from cost and selling price
  const calculateMarkup = (costExcl, sellingIncl, vatCategory) => {
    if (!costExcl || costExcl <= 0 || !sellingIncl || sellingIncl <= 0) return 0;
    const vatRate = getVatRate(vatCategory);
    const sellingExcl = sellingIncl / (1 + vatRate);
    const markup = ((sellingExcl - costExcl) / costExcl) * 100;
    return Math.max(0, Math.round(markup * 100) / 100);
  };

  // Calculate selling price from cost and markup
  const calculateSellingPrice = (costExcl, markupPercent, vatCategory) => {
    if (!costExcl || costExcl <= 0) return 0;
    const vatRate = getVatRate(vatCategory);
    const sellingExcl = costExcl * (1 + markupPercent / 100);
    const sellingIncl = sellingExcl * (1 + vatRate);
    return Math.round(sellingIncl * 100) / 100;
  };
  
  // Get the cost price to use for markup calculations (prefer average cost if available)
  // The transport_cost is treated as part of the landed cost — when set, it is
  // added to the goods cost so markup is applied to the true total.
  const getCostForMarkup = () => {
    const avgCost = parseFloat(formData.average_cost_price_excl_vat) || 0;
    const lastCost = parseFloat(localCostPrice) || 0;
    const transport = parseFloat(formData.transport_cost) || 0;
    // Use average cost if available and greater than 0, otherwise use last cost
    const goods = avgCost > 0 ? avgCost : lastCost;
    return goods + transport;
  };

  // Handle a change in Transport Cost — recompute the selling price from the
  // current markup % using the new transport-adjusted cost. This means typing
  // a transport number into the field immediately bumps the selling price.
  const handleTransportCostChange = (val) => {
    const transport = val === "" ? 0 : parseFloat(val) || 0;
    handleChange("transport_cost", transport);

    const avgCost = parseFloat(formData.average_cost_price_excl_vat) || 0;
    const lastCost = parseFloat(localCostPrice) || 0;
    const goods = avgCost > 0 ? avgCost : lastCost;
    const effectiveCost = goods + transport;
    const markup = parseFloat(localMarkup) || 0;
    if (effectiveCost > 0 && markup > 0 && !priceLocked) {
      const newSellingPrice = calculateSellingPrice(effectiveCost, markup);
      setLocalSellingPrice(newSellingPrice);
      handleChange("selling_price_incl_vat", newSellingPrice);
      handleChange("retail_price_level1", newSellingPrice);
    }
  };
  
  // Sync local state when formData changes from outside
  useEffect(() => {
    setLocalMarkup(formData.markup_percent ?? "");
  }, [formData.markup_percent]);
  
  useEffect(() => {
    setLocalSellingPrice(formData.selling_price_incl_vat || formData.retail_price_level1 || "");
  }, [formData.selling_price_incl_vat, formData.retail_price_level1]);
  
  useEffect(() => {
    setLocalCostPrice(formData.last_cost_price_excl_vat || formData.cost_price || "");
  }, [formData.last_cost_price_excl_vat, formData.cost_price]);
  
  // Recalculate markup when average cost changes
  useEffect(() => {
    const avgCost = parseFloat(formData.average_cost_price_excl_vat) || 0;
    const sellingPrice = parseFloat(localSellingPrice) || 0;
    if (avgCost > 0 && sellingPrice > 0) {
      const vatRate = getVatRate(formData.vat_category);
      const sellingExcl = sellingPrice / (1 + vatRate);
      const newMarkup = Math.max(0, Math.round(((sellingExcl - avgCost) / avgCost) * 100 * 100) / 100);
      setLocalMarkup(newMarkup);
      handleChange("markup_percent", newMarkup);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.average_cost_price_excl_vat]);

  // Handle selling price blur - recalculate markup based on average cost (or last cost if no average)
  const handleSellingPriceBlur = () => {
    const sellingPrice = localSellingPrice === "" ? 0 : parseFloat(localSellingPrice);
    handleChange("selling_price_incl_vat", sellingPrice);
    handleChange("retail_price_level1", sellingPrice);
    
    // Recalculate markup from the user-entered price (markup derives from price, NOT the other way)
    const costForMarkup = getCostForMarkup();
    if (costForMarkup > 0 && sellingPrice > 0) {
      const newMarkup = calculateMarkup(costForMarkup, sellingPrice);
      setLocalMarkup(newMarkup);
      // Use _fromPrice flag so ProductDetailModal doesn't recalculate selling price from this markup
      handleChange("markup_percent_from_price", newMarkup);
      
      if (newMarkup < 7) {
        handleChange("discount_percent_allowed", 0);
        handleChange("no_discount", true);
      } else {
        const currentDiscount = formData.discount_percent_allowed || 0;
        if (currentDiscount === 0) {
          const suggestedDiscount = newMarkup <= 20 ? 5 : newMarkup <= 30 ? 7.5 : 10;
          handleChange("discount_percent_allowed", suggestedDiscount);
        }
        handleChange("no_discount", false);
      }
    }
  };

  // Handle cost price blur - recalculate markup based on average cost (or last cost if no average)
  const handleCostPriceBlur = () => {
    const costPrice = localCostPrice === "" ? 0 : parseFloat(localCostPrice);
    handleChange("last_cost_price_excl_vat", costPrice);
    handleChange("cost_price", costPrice);
    
    const sellingPrice = parseFloat(localSellingPrice) || 0;
    // Use average cost for markup calculation if available, otherwise use the new last cost.
    // Add transport_cost so the markup % reflects the true landed cost.
    const avgCost = parseFloat(formData.average_cost_price_excl_vat) || 0;
    const transport = parseFloat(formData.transport_cost) || 0;
    const costForMarkup = (avgCost > 0 ? avgCost : costPrice) + transport;
    
    if (costForMarkup > 0 && sellingPrice > 0) {
      const newMarkup = calculateMarkup(costForMarkup, sellingPrice);
      setLocalMarkup(newMarkup);
      handleChange("markup_percent", newMarkup);
      
      if (newMarkup < 7) {
        handleChange("discount_percent_allowed", 0);
        handleChange("no_discount", true);
      } else {
        // Auto-set discount based on markup if not already set
        const currentDiscount = formData.discount_percent_allowed || 0;
        if (currentDiscount === 0) {
          const suggestedDiscount = newMarkup <= 20 ? 5 : newMarkup <= 30 ? 7.5 : 10;
          handleChange("discount_percent_allowed", suggestedDiscount);
        }
        handleChange("no_discount", false);
      }
    }
  };

  // Handle markup blur - recalculate selling price based on average cost (or last cost if no average)
  const handleMarkupBlur = () => {
    const markup = localMarkup === "" ? 0 : parseFloat(localMarkup);

    if (!Number.isNaN(markup) && markup < 0) {
      toast.error("Markup % cannot be negative");
      return;
    }
    
    handleChange("markup_percent", markup);
    
    // Use average cost for selling price calculation if available
    const costForMarkup = getCostForMarkup();
    if (costForMarkup > 0) {
      const newSellingPrice = calculateSellingPrice(costForMarkup, markup);
      setLocalSellingPrice(newSellingPrice);
      handleChange("selling_price_incl_vat", newSellingPrice);
      handleChange("retail_price_level1", newSellingPrice);
    }
    
    if (markup < 7) {
      handleChange("discount_percent_allowed", 0);
      handleChange("no_discount", true);
    } else {
      // Auto-set discount based on markup if not already set
      const currentDiscount = formData.discount_percent_allowed || 0;
      if (currentDiscount === 0) {
        const suggestedDiscount = markup <= 20 ? 5 : markup <= 30 ? 7.5 : 10;
        handleChange("discount_percent_allowed", suggestedDiscount);
      }
      handleChange("no_discount", false);
    }
  };

  // Current markup value for display
  const currentMarkup = parseFloat(localMarkup) || 0;
  const isLowMarkup = currentMarkup < 7 && currentMarkup > 0;
  
  // Determine which cost is being used for markup calculation
  const avgCost = parseFloat(formData.average_cost_price_excl_vat) || 0;
  const usingAvgCost = avgCost > 0;

  return (
    <div className="space-y-4">
      {/* Markup Review Banner — product was auto-flagged as below the markup floor */}
      {formData.needs_markup_review && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3" data-testid="markup-review-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-900 text-sm font-semibold">
                Markup dropped below floor — please review
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Current markup is <strong>{(formData.markup_review_new_markup ?? formData.markup_percent ?? 0).toFixed(2)}%</strong>
                {formData.markup_review_floor != null && (
                  <> (floor: <strong>{formData.markup_review_floor}%</strong>)</>
                )}
                {formData.markup_review_source && (
                  <> — flagged by <strong>{
                    ({grv:"GRV", manual:"manual edit", csv_price_import:"CSV price import", price_suggestion:"Price Suggestion", fix_markups:"Fix Markups"}[formData.markup_review_source] || formData.markup_review_source)
                  }</strong></>
                )}.
                Fix the price on this tab, or open <a href="/price-changes" className="underline font-medium">Price Audit</a> to confirm this is intentional.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Low Markup Warning */}
      {isLowMarkup && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <span className="text-red-700 text-sm font-medium">
            Low Markup Warning: {currentMarkup.toFixed(2)}% markup - No discounts allowed on this item
          </span>
        </div>
      )}

      <label
        className={`flex items-start gap-3 rounded-lg border p-3 ${priceLocked ? "border-sky-400 bg-sky-50" : "border-slate-200"} ${isAdmin ? "cursor-pointer" : "opacity-80"}`}
        data-testid="price-lock-toggle"
      >
        <Checkbox
          checked={priceLocked}
          disabled={!isAdmin}
          onCheckedChange={(v) => handleChange("price_locked", !!v)}
          data-testid="price-lock-checkbox"
        />
        <div>
          <p className="text-sm font-semibold flex items-center gap-1">
            <Lock className="h-4 w-4 text-sky-600" /> Freeze selling price
          </p>
          <p className="text-xs text-slate-600">
            GRVs, imports and price updates will not change this price. Only an admin can change or unfreeze it.
          </p>
        </div>
      </label>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="flex items-center gap-1">
            Selling Price (Incl VAT)
            {priceLocked && <Lock className="h-3.5 w-3.5 text-sky-600" data-testid="pricing-tab-lock-icon" />}
          </Label>
          <Input
            type="number"
            step={(formData.direct_order || formData.skip_price_rounding) ? "0.01" : "0.10"}
            value={localSellingPrice}
            onChange={(e) => setLocalSellingPrice(e.target.value)}
            onBlur={handleSellingPriceBlur}
            disabled={priceReadOnly}
            data-testid="pricing-selling-price-input"
            placeholder={(formData.direct_order || formData.skip_price_rounding) ? "0.00" : "0.00"}
          />
        </div>
        <div>
          <Label>VAT-Exempt Selling Price (Incl VAT)</Label>
          <Input
            type="number"
            step="0.01"
            value={
              formData.vat_exempt_price_incl_vat
              ?? formData.promotion_price_incl_vat
              ?? ""
            }
            onChange={(e) => handleChange("vat_exempt_price_incl_vat", e.target.value === "" ? null : parseFloat(e.target.value))}
            placeholder={(() => {
              const shelf = Number(formData.selling_price_incl_vat) || 0;
              return shelf > 0 ? `Default: R ${shelf.toFixed(2)}` : "0.00";
            })()}
            data-testid="vat-exempt-price-input"
          />
          {(() => {
            const special = Number(
              formData.vat_exempt_price_incl_vat ?? formData.promotion_price_incl_vat ?? 0
            );
            if (special > 0) {
              return (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1">
                  Exempt customer pays <strong>R {(special / 1.15).toFixed(2)}</strong> (ex-VAT).
                </p>
              );
            }
            return null;
          })()}
          <p className="text-xs text-slate-500 mt-1">
            Optional. Entered <em>incl VAT</em> — the till strips 15% before charging the customer.
            Applies ONLY when the customer holds a valid VAT-Exemption certificate <em>and</em>
            this product's VAT category is <strong>Exempt</strong> or
            <strong>&nbsp;VAT Exempt Eligible</strong>. Leave blank to fall back to auto-strip of
            the shelf price.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Last Cost Price (Excl VAT)</Label>
          <Input
            type="number"
            step="0.01"
            value={localCostPrice}
            onChange={(e) => setLocalCostPrice(e.target.value)}
            onBlur={handleCostPriceBlur}
            placeholder="0.00"
          />
        </div>
        <div>
          <Label>Average Cost Price (Excl VAT) - Auto Calculated</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.average_cost_price_excl_vat || ""}
            onChange={(e) => handleChange("average_cost_price_excl_vat", e.target.value === "" ? 0 : parseFloat(e.target.value))}
            placeholder="0.00"
            className="bg-gray-50"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Markup %</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={localMarkup}
            disabled={priceReadOnly}
            onChange={(e) => {
              const nextValue = e.target.value;
              if (nextValue === "" || parseFloat(nextValue) >= 0) {
                setLocalMarkup(nextValue);
              } else {
                toast.error("Markup % cannot be negative");
              }
            }}
            onBlur={handleMarkupBlur}
            placeholder="0.00"
            className={isLowMarkup ? "border-red-400 bg-red-50" : ""}
            data-testid="product-pricing-markup-input"
          />
          {isLowMarkup ? (
            <p className="text-xs text-red-600 mt-1 font-medium">
              Markup under 7% - discounts disabled
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              Based on {usingAvgCost ? "Avg Cost" : "Last Cost"}: R{getCostForMarkup().toFixed(2)}
              {(parseFloat(formData.transport_cost) || 0) > 0 && (
                <span> (incl. R{(parseFloat(formData.transport_cost) || 0).toFixed(2)} transport)</span>
              )}
            </p>
          )}
        </div>
        <div>
          <Label>Max Discount % Allowed</Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={formData.discount_percent_allowed || ""}
            onChange={(e) => handleChange("discount_percent_allowed", e.target.value === "" ? 0 : parseFloat(e.target.value))}
            placeholder="0.00"
            disabled={isLowMarkup}
            className={isLowMarkup ? "bg-gray-100 cursor-not-allowed" : ""}
          />
          <p className="text-xs text-gray-500 mt-1">
            {isLowMarkup ? (
              <span className="text-red-600">Disabled - markup too low</span>
            ) : currentMarkup > 0 ? (
              <>Suggested: {currentMarkup <= 20 ? '5%' : currentMarkup <= 30 ? '7.5%' : '10%'} 
              <span className="text-gray-400 ml-1">(based on {currentMarkup.toFixed(1)}% markup)</span></>
            ) : 'Set markup to auto-calculate'}
          </p>
        </div>
        <div>
          <Label>VAT Rate</Label>
          <Select 
            value={formData.vat_category || "Standard 15%"} 
            onValueChange={(value) => {
              handleChange("vat_category", value);
              // Recalculate markup when VAT changes
              const costPrice = parseFloat(localCostPrice) || 0;
              const sellingPrice = parseFloat(localSellingPrice) || 0;
              if (costPrice > 0 && sellingPrice > 0) {
                const newMarkup = calculateMarkup(costPrice, sellingPrice, value);
                setLocalMarkup(newMarkup);
                handleChange("markup_percent", newMarkup);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select VAT rate" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Standard 15%">15% (Standard)</SelectItem>
              <SelectItem value="Zero-rated 0%">0% (Zero Rated)</SelectItem>
              <SelectItem value="Exempt">Exempt (customer-conditional)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1 leading-snug">
            <strong>Zero-rated:</strong> 0% VAT for everyone (basic food).<br/>
            <strong>Exempt:</strong> Shelf price still includes 15% VAT. VAT is only stripped at the till when the customer has a VAT&nbsp;Exemption Certificate ticked on their profile.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Transport Cost</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.transport_cost || ""}
            onChange={(e) => handleTransportCostChange(e.target.value)}
            placeholder="0.00"
            data-testid="product-transport-cost-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Added to the goods cost when calculating markup &amp; selling price. Editing this value
            auto-adjusts the selling price using the current markup %.
          </p>
        </div>
      </div>

      {/* No Discount Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.no_discount ? 'bg-orange-50 border-orange-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="no_discount"
            checked={formData.no_discount || false}
            onCheckedChange={(checked) => handleChange("no_discount", checked)}
            data-testid="no-discount-checkbox"
          />
          <Label htmlFor="no_discount" className={`font-medium ${formData.no_discount ? 'text-orange-700' : 'text-gray-700'}`}>
            No Discount - Exempt from customer level discounts
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          When checked, this product will NOT receive automatic discounts based on customer level (Level 1 = 2%, Level 2 = 5%).
        </p>
      </div>

      {/* Staff-only Discount */}
      <div className={`border rounded-lg p-4 ${(parseFloat(formData.staff_discount_percent) || 0) > 0 ? 'bg-indigo-50 border-indigo-300' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-3">
          <Label htmlFor="staff_discount_percent" className={`font-medium min-w-[180px] ${(parseFloat(formData.staff_discount_percent) || 0) > 0 ? 'text-indigo-700' : 'text-gray-700'}`}>
            Staff Discount %
          </Label>
          <Input
            id="staff_discount_percent"
            data-testid="staff-discount-percent-input"
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={formData.staff_discount_percent ?? ""}
            onChange={(e) => handleChange("staff_discount_percent", e.target.value === "" ? 0 : parseFloat(e.target.value))}
            placeholder="0"
            className="w-32"
          />
          <span className="text-xs text-gray-500">%</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Extra discount applied automatically at the till <strong>only when the selected customer is flagged as Staff</strong>.
          Replaces any promo, loyalty or level discount on this line — highest single discount wins.
          Leave at 0 to give staff the same price as everyone else.
        </p>
      </div>

      {/* Sold per KG Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.sold_per_kg ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="sold_per_kg"
            checked={formData.sold_per_kg || false}
            onCheckedChange={(checked) => handleChange("sold_per_kg", checked)}
            data-testid="sold-per-kg-checkbox"
          />
          <Label htmlFor="sold_per_kg" className={`font-medium ${formData.sold_per_kg ? 'text-blue-700' : 'text-gray-700'}`}>
            Sold per KG - Allow decimal quantities
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Enable this for products sold by weight (e.g., 1.5 kg, 2.75 kg). Quantities can be entered with up to 3 decimal places.
        </p>
      </div>

      {/* Direct Order Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.direct_order ? 'bg-purple-50 border-purple-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="direct_order"
            checked={formData.direct_order || false}
            onCheckedChange={(checked) => handleChange("direct_order", checked)}
            data-testid="direct-order-checkbox"
          />
          <Label htmlFor="direct_order" className={`font-medium ${formData.direct_order ? 'text-purple-700' : 'text-gray-700'}`}>
            Direct Order - Special order for customer
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Enable this for products ordered specifically for a customer. These products are NOT kept in regular stock and will be excluded from the auto-order program.
        </p>
      </div>

      {/* Internal Consumable Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.is_internal_consumable ? 'bg-purple-50 border-purple-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_internal_consumable"
            checked={formData.is_internal_consumable || false}
            onCheckedChange={(checked) => handleChange("is_internal_consumable", checked)}
            data-testid="internal-consumable-checkbox"
          />
          <Label htmlFor="is_internal_consumable" className={`font-medium ${formData.is_internal_consumable ? 'text-purple-700' : 'text-gray-700'}`}>
            Internal Consumable — used by the store (till rolls, paper, toner)
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Hides this product from the main Stock Master / saleable lists and routes future GRV costs to the <em>Operating Supplies</em> expense line on the Income Statement (instead of inventory). Reorder alerts still apply via the <em>Internal Supplies</em> page.
        </p>
      </div>

      {/* Skip Price Rounding Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.skip_price_rounding ? 'bg-teal-50 border-teal-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="skip_price_rounding"
            checked={formData.skip_price_rounding || false}
            onCheckedChange={(checked) => handleChange("skip_price_rounding", checked)}
            data-testid="skip-rounding-checkbox"
          />
          <Label htmlFor="skip_price_rounding" className={`font-medium ${formData.skip_price_rounding ? 'text-teal-700' : 'text-gray-700'}`}>
            Skip Price Rounding - Allow exact cents
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          By default, prices are rounded to the nearest 10 cents (e.g. R2.10). Enable this to allow exact cent pricing (e.g. R2.12, R0.23).
        </p>
      </div>

      {/* Consignment Stock Checkbox */}
      <div className={`border rounded-lg p-4 ${formData.is_consignment ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="is_consignment"
            checked={formData.is_consignment || false}
            onCheckedChange={(checked) => handleChange("is_consignment", checked)}
            data-testid="consignment-checkbox"
          />
          <Label htmlFor="is_consignment" className={`font-medium ${formData.is_consignment ? 'text-amber-700' : 'text-gray-700'}`}>
            Consignment Stock
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Stock owned by supplier until sold. You only pay for items when they are sold to customers.
        </p>
        
        {formData.is_consignment && (
          <div className="mt-3 ml-6 space-y-2">
            <Label className="text-sm">Consignment Supplier</Label>
            <Select 
              value={formData.consignment_supplier_id || formData.supplier_id || ""} 
              onValueChange={(value) => handleChange("consignment_supplier_id", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select supplier who owns this stock" />
              </SelectTrigger>
              <SelectContent>
                {[...suppliersList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-amber-600">
              When this product is sold, the cost will be tracked for payment to this supplier.
            </p>
          </div>
        )}
      </div>

      {/* Case Link */}
      <CaseLinkSection formData={formData} handleChange={handleChange} />
    </div>
  );
};

// Sub-component for case linking with product search
import { useState as useLocalState } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { Package, Search, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaseLinkSection = ({ formData, handleChange }) => {
  const [searchQuery, setSearchQuery] = useLocalState("");
  const [searchResults, setSearchResults] = useLocalState([]);
  const [searching, setSearching] = useLocalState(false);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(q)}&limit=8`);
      const items = (res.data.products || res.data || []).filter(p => p.id !== formData.id);
      setSearchResults(items);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const selectLinkedProduct = (product) => {
    handleChange("is_case_product", true);
    handleChange("linked_product_id", product.id);
    handleChange("linked_product_name", product.name);
    setSearchQuery("");
    setSearchResults([]);
  };

  const clearLink = () => {
    handleChange("is_case_product", false);
    handleChange("linked_product_id", "");
    handleChange("linked_product_name", "");
    handleChange("units_per_case", "");
  };

  return (
    <div className={`border rounded-lg p-4 ${formData.is_case_product ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_case_product"
          checked={formData.is_case_product || false}
          onCheckedChange={(checked) => {
            handleChange("is_case_product", checked);
            if (!checked) clearLink();
          }}
          data-testid="case-product-checkbox"
        />
        <Label htmlFor="is_case_product" className={`font-medium flex items-center gap-1 ${formData.is_case_product ? 'text-blue-700' : 'text-gray-700'}`}>
          <Package className="h-4 w-4" />
          Case Product - Linked to loose item
        </Label>
      </div>
      <p className="text-xs text-gray-500 mt-2 ml-6">
        Enable if this product is a case/pack. Stock will be deducted from the linked loose product when sold.
      </p>

      {formData.is_case_product && (
        <div className="mt-3 ml-6 space-y-3">
          {/* Linked product display or search */}
          {formData.linked_product_id ? (
            <div className="flex items-center justify-between bg-white border rounded-lg p-3">
              <div>
                <p className="text-xs text-gray-500">Linked Loose Product</p>
                <p className="font-medium text-blue-700">{formData.linked_product_name || formData.linked_product_id}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={clearLink} className="text-red-500 h-7">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Label className="text-sm">Search for the loose product to link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Type product name or PLU..."
                  data-testid="case-link-search"
                />
                {searching && <div className="absolute right-3 top-8"><div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" /></div>}
              </div>
              {searchResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {searchResults.map(p => (
                    <button
                      key={p.id}
                      onClick={() => selectLinkedProduct(p)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between border-b last:border-0 text-sm"
                    >
                      <div>
                        <span className="font-medium">{p.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{p.plu_code}</span>
                      </div>
                      <span className="text-xs text-gray-500">Stock: {p.current_stock}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Units per case */}
          <div>
            <Label className="text-sm">Units per Case</Label>
            <Input
              type="number"
              min="1"
              value={formData.units_per_case || ""}
              onChange={(e) => handleChange("units_per_case", parseInt(e.target.value) || "")}
              placeholder="e.g. 24"
              className="w-32 mt-1"
              data-testid="units-per-case-input"
            />
            <p className="text-xs text-blue-600 mt-1">
              When 1 case is sold, {formData.units_per_case || '?'} units will be deducted from the linked product's stock.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
